import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import "../../assets/css/mychat.css";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faUser,
  faTimes,
  faPaperclip,
  faSpinner,
  faBars,
  faArrowUp,
  faArrowDown,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";
import { useLocation } from "react-router-dom";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  serverTimestamp,
  where,
  doc,
  Timestamp,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../config/firebase";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import {
  DashboardURL,
  handleMessageNotification,
  ImageURL,
} from "../config/config";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import "tippy.js/dist/tippy.css";
import Tippy from "@tippyjs/react";
import debounce from "lodash/debounce";
import {
  PII_PHONE_REGEX,
  PII_EMAIL_REGEX,
  PII_URL_SOCIAL_REGEX,
  PII_ADDRESS_REGEX,
  CODE_REGEX,
} from "../../utility/commonFunctions";

function MyChat({ initialChats = [] }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [chats, setChats] = useState(initialChats || []);
  const [filteredChats, setFilteredChats] = useState([]);
  const [noResults, setNoResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isChatListOpen, setIsChatListOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const chatBottomRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const searchInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const [otherUser, setOtherUser] = useState(null);
  const [isMessageSearching, setIsMessageSearching] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const location = useLocation();
  const LoginData = useSelector((state) =>
    state.login.data && state.login.data.chefDetails ? state.login.data : {}
  );
  const { roomId } = location?.state || {};
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [noHighlightsMessage, setNoHighlightsMessage] = useState("");
  const [visible, setVisible] = useState(false);

  const [chef, setChef] = useState(LoginData?.chefDetails || {});

  const [messageSearchTerm, setMessageSearchTerm] = useState("");
  const [searchHighlights, setSearchHighlights] = useState([]);
  const [currentHighlightIndex, setCurrentHighlightIndex] = useState(-1);

  const [selectedImages, setSelectedImages] = useState([]);
  const fileInputRef = useRef(null);
  const [hasRooms, setHasRooms] = useState(false);
  const previousDayRef = useRef(null);
  const inputRef = useRef(null);
  const [textAreaHeight, setTextAreaHeight] = useState("45px");
  const [restrictedMessages, setRestrictedMessages] = useState([]);
  const maxTextAreaHeight = "80px";
  const [chatListLoading, setChatListLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [userChatHistoryTime, setUserChatHistoryTime] = useState(1);

  useEffect(() => {
    if (LoginData?.chefDetails) {
      setChef(LoginData.chefDetails);
    }
  }, [LoginData]);

  const [roomDetails, setRoomDetails] = useState([]);

  const fetchUserChatHistoryTime = async () => {
    try {
      const response = await axiosInstance().get(DashboardURL, {});
      const timeFromApi = Number(response.data.data.userChatHistoryTime || "1");
      setUserChatHistoryTime(timeFromApi);
      return timeFromApi;
    } catch (error) {
      console.error("Error fetching userChatHistoryTime:", error);
      return 1;
    }
  };

  useEffect(() => {
    if (!LoginData.chefDetails._id) return;

    const roomsCollection = collection(db, "rooms");
    const q = query(
      roomsCollection,
      where("secondUserId", "==", LoginData.chefDetails._id)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const roomData = snapshot.docs.map((doc) => ({
          _id: doc.id,
          ...doc.data(),
        }));
        setRoomDetails(roomData);
        setHasRooms(roomData.length > 0);
      },
      (error) => {
        new Error("Error fetching rooms: " + error);
      }
    );

    return () => unsubscribe();
  }, [LoginData.chefDetails._id]);

  useEffect(() => {
    const loadChatData = async () => {
      if (roomDetails.length > 0) {
        const updatedChats = [...chats];
        let unsubscribeFunctions = [];
        const userChatHistoryTimeValue = await fetchUserChatHistoryTime();

        roomDetails.forEach(async (room) => {
          const otherUserId = room.firstUserId;
          const messagesRef = collection(db, "rooms", room._id, "messages");
          const messagesQuery = query(messagesRef, limit(1));
          const messagesSnapshot = await getDocs(messagesQuery);

          const isOnChatPageWithIds =
            location.pathname === "/dashboard/mychats" &&
            location.state?.userId &&
            location.state?.roomId;

          if (messagesSnapshot.empty && !isOnChatPageWithIds) {
            return;
          }
          const unsubscribeMessages = onSnapshot(
            query(messagesRef, orderBy("timestamp", "asc")),
            (messagesSnapshot) => {
              let lastMessage = null;
              let lastMessageCreatedAt = null;
              let lastMessageObj = null;
              let unseenMessagesCount = 0;
              const messagesData = messagesSnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              const msgs = messagesData;

              const cutoffDate = moment().subtract(
                userChatHistoryTimeValue,
                "days"
              );
              const filteredMsgs = msgs.filter((msg) =>
                moment(msg.timestamp?.toDate()).isAfter(cutoffDate)
              );

              if (filteredMsgs.length > 0) {
                let lastNonRestrictedMessage = null;
                for (let i = filteredMsgs.length - 1; i >= 0; i--) {
                  if (!filteredMsgs[i].isRestricted) {
                    lastNonRestrictedMessage = filteredMsgs[i];
                    break;
                  }
                }
                const lastMsg = filteredMsgs[filteredMsgs.length - 1];
                if (lastMsg.isRestricted) {
                  if (lastNonRestrictedMessage) {
                    const hasImage =
                      lastNonRestrictedMessage.imagePath?.length > 0 ||
                      lastNonRestrictedMessage.imageUrls?.length > 0;
                    const hasMessage =
                      lastNonRestrictedMessage.message?.trim()?.length > 0;

                    lastMessage = hasImage
                      ? "[Photo]"
                      : hasMessage
                      ? lastNonRestrictedMessage.message
                      : "No message";
                    lastMessageObj = lastNonRestrictedMessage;
                    lastMessageCreatedAt =
                      lastNonRestrictedMessage.timestamp?.toDate();
                  } else {
                    lastMessage = "No message";
                    lastMessageObj = null;
                    lastMessageCreatedAt = null;
                  }
                } else {
                  const hasImage =
                    lastMsg.imagePath?.length > 0 ||
                    lastMsg.imageUrls?.length > 0;
                  const hasMessage = lastMsg.message?.trim()?.length > 0;
                  lastMessage = hasImage
                    ? "[Photo]"
                    : hasMessage
                    ? lastMsg.message
                    : "No message";
                  lastMessageObj = lastMsg;
                  lastMessageCreatedAt =
                    filteredMsgs[filteredMsgs.length - 1].timestamp?.toDate();
                }
                unseenMessagesCount = filteredMsgs.filter(
                  (msg) =>
                    !msg.isSeen &&
                    msg.receiverId === LoginData.chefDetails._id &&
                    !msg.isRestricted
                ).length;
              } else {
                lastMessage = "No message";
                lastMessageObj = null;
                lastMessageCreatedAt = null;
                unseenMessagesCount = 0;
              }

              const updatedRoom = {
                ...room,
                lastMessage,
                lastMessageCreatedAt,
                unseenMessagesCount,
                otherUserId,
                lastMessageObj,
              };

              const existingChatIndex = updatedChats.findIndex(
                (chat) => chat._id === room._id
              );

              if (existingChatIndex > -1) {
                updatedChats[existingChatIndex] = updatedRoom;
                updatedChats.sort((a, b) => {
                  if (a.unseenMessagesCount > 0 && b.unseenMessagesCount === 0)
                    return -1;
                  if (a.unseenMessagesCount === 0 && b.unseenMessagesCount > 0)
                    return 1;
                  if (!a.lastMessageCreatedAt) return 1;
                  if (!b.lastMessageCreatedAt) return -1;
                  return b.lastMessageCreatedAt - a.lastMessageCreatedAt;
                });
              } else {
                updatedChats.push(updatedRoom);
                updatedChats.sort((a, b) => {
                  if (a.unseenMessagesCount > 0 && b.unseenMessagesCount === 0)
                    return -1;
                  if (a.unseenMessagesCount === 0 && b.unseenMessagesCount > 0)
                    return 1;
                  if (!a.lastMessageCreatedAt) return 1;
                  if (!b.lastMessageCreatedAt) return -1;
                  return b.lastMessageCreatedAt - a.lastMessageCreatedAt;
                });
              }
              setChats([...updatedChats]);
              setFilteredChats([...updatedChats]);
              setChatListLoading(false);
              setHasLoaded(true); //Chats are loaded
            },
            (error) => {
              new Error("Error fetching messages: " + error);
              setChatListLoading(false);
              setHasLoaded(true); //Even if there's an error, consider loading complete
            }
          );

          unsubscribeFunctions.push(unsubscribeMessages);
        });

        return () => {
          unsubscribeFunctions.forEach((unsubscribe) => unsubscribe());
        };
      }
    };

    loadChatData();
  }, [roomDetails, LoginData.chefDetails._id]);

  useEffect(() => {
    if (isInitialLoad && chats.length > 0) {
      let initialChat;

      if (roomId) {
        initialChat = chats.find((chat) => chat._id === roomId);
      }

      if (!initialChat) {
        const sortedChats = [...chats].sort((a, b) => {
          if (a.unseenMessagesCount > 0 && b.unseenMessagesCount === 0)
            return -1;
          if (a.unseenMessagesCount === 0 && b.unseenMessagesCount > 0)
            return 1;
          if (!a.lastMessageCreatedAt) return 1;
          if (!b.lastMessageCreatedAt) return -1;
          return b.lastMessageCreatedAt - a.lastMessageCreatedAt;
        });

        initialChat = sortedChats[0];
      }

      if (initialChat) {
        setSelectedChatId(initialChat._id);
        setOtherUser(initialChat);
      }

      setTimeout(() => {
        setIsInitialLoad(false);
      }, 3000);
    }
  }, [chats, isInitialLoad, roomId]);

  useEffect(() => {
    if (!isSearching) {
      setFilteredChats(chats);
    }
  }, [chats, isSearching]);

  useEffect(() => {
    if (selectedChatId) {
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  }, [selectedChatId]);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm, currentChats) => {
        if (searchTerm.trim()) {
          const filtered = currentChats.filter((chat) =>
            chat.firstUserName
              ?.trim()
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );
          setFilteredChats(filtered);
          setNoResults(filtered.length === 0);
        } else {
          setFilteredChats(currentChats);
          setNoResults(false);
        }
      }, 200),
    []
  );

  const handleSearchChange = useCallback(
    (event) => {
      const term = event.target.value;
      setSearchTerm(term);
      debouncedSearch(term, chats);
    },
    [chats, debouncedSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userChatHistoryTimeFromApi = await fetchUserChatHistoryTime();

        if (selectedChatId) {
          const messagesRef = collection(
            db,
            "rooms",
            selectedChatId,
            "messages"
          );

          const startDate = new Date();
          startDate.setDate(startDate.getDate() - userChatHistoryTimeFromApi);

          const q = query(
            messagesRef,
            where("timestamp", ">=", startDate),
            orderBy("timestamp", "asc")
          );

          const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
              const msgs = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
              }));
              setMessages(msgs);
              setMessageSearchTerm("");
              setSearchHighlights([]);
            },
            (error) => {
              new Error("Error fetching messages:" + error);
            }
          );

          return unsubscribe;
        }
      } catch (error) {
        new Error("Error in fetchData:" + error);
      }
    };

    let unsubscribeFn;

    fetchData().then((unsubscribe) => {
      if (typeof unsubscribe === "function") {
        unsubscribeFn = unsubscribe;
      }
    });

    return () => {
      if (unsubscribeFn) {
        unsubscribeFn();
      }
    };
  }, [selectedChatId]);

  const handleChatItemClick = (chatId) => {
    setSelectedChatId(chatId);
    setCurrentHighlightIndex(-1);
    const chatData = chats.find((chat) => chat._id === chatId);
    if (chatData) {
      setOtherUser(chatData);
      if (window.innerWidth <= 768 && isChatListOpen) {
        setIsChatListOpen(false);
      }
      return;
    }
  };

  useEffect(() => {
    const markMessagesAsRead = async () => {
      if (selectedChatId) {
        const messagesToUpdate = messages.filter(
          (msg) => !msg.isSeen && msg.receiverId === LoginData.chefDetails._id
        );

        try {
          const updatePromises = messagesToUpdate.map((msg) => {
            const messageDocRef = doc(
              db,
              "rooms",
              selectedChatId,
              "messages",
              msg.id
            );
            return updateDoc(messageDocRef, { isSeen: true });
          });

          await Promise.all(updatePromises);
        } catch (error) {
          new Error("Error updating message:" + error);
        }
      }
    };

    if (messages.length > 0) {
      markMessagesAsRead();
    }
  }, [messages, selectedChatId, LoginData.chefDetails._id]);

  const toggleChatList = () => {
    setIsChatListOpen(!isChatListOpen);
  };

  const handleInputChange = (event) => {
    const text = event.target.value;
    if (text.length <= 1000) {
      setNewMessage(text);

      inputRef.current.style.height =
        Math.min(
          inputRef.current.scrollHeight,
          parseInt(maxTextAreaHeight, 10)
        ) + "px";
      if (!text) {
        inputRef.current.style.height = "45px";
      }
      setTextAreaHeight(inputRef.current.style.height);
    } else {
      toast.warn("Your message exceeds the character limit (1,000).", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };
  const containsRestrictedContent = (text) => {
    PII_PHONE_REGEX.lastIndex = 0;
    PII_EMAIL_REGEX.lastIndex = 0;
    PII_URL_SOCIAL_REGEX.lastIndex = 0;
    PII_ADDRESS_REGEX.lastIndex = 0;
    if (CODE_REGEX.global) CODE_REGEX.lastIndex = 0;

    if (PII_PHONE_REGEX.test(text)) {
      return true;
    }
    if (PII_EMAIL_REGEX.test(text)) {
      return true;
    }
    if (PII_URL_SOCIAL_REGEX.test(text)) {
      return true;
    }
    if (PII_ADDRESS_REGEX.test(text)) {
      return true;
    }
    if (CODE_REGEX.test(text)) {
      return true;
    }

    return false;
  };

  const handleEmojiClick = (emoji) => {
    if (newMessage.length + emoji.native.length <= 1000) {
      setNewMessage((prev) => prev + emoji.native);
      inputRef.current.style.height =
        Math.min(
          inputRef.current.scrollHeight,
          parseInt(maxTextAreaHeight, 10)
        ) + "px";
      setTextAreaHeight(inputRef.current.style.height);
      inputRef.current.focus();
    } else {
      toast.warn("Your message exceeds the character limit (1,000).", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  };

  const generateUniqueId = () => {
    return `temp_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  };

  const sendMessage = async () => {
    const isRestricted = containsRestrictedContent(newMessage);
    const userId = LoginData.chefDetails._id;
    const chefId = otherUser.firstUserId;
    const now = new Date();

    const seconds = Math.floor(now.getTime() / 1000);
    const nanoseconds = now.getMilliseconds() * 1000000;

    const timestamp = new Timestamp(seconds, nanoseconds);

    if (isRestricted) {
      const tempId = `restricted_${Date.now()}`;
      setRestrictedMessages((prev) => [
        ...prev,
        {
          id: tempId,
          senderId: userId,
          receiverId: chefId,
          message: newMessage,
          timestamp,
          imageUrls: [],
          isSeen: false,
          isSending: false,
          isFirstMessage: false,
          isNotification: false,
          isSecondNotification: false,
          isRestricted: true,
          isTemp: true,
        },
      ]);
      setNewMessage("");
      setTextAreaHeight("45px");
      setSelectedImages([]);

      const messagesRef = collection(db, "rooms", selectedChatId, "messages");
      await addDoc(messagesRef, {
        senderId: userId,
        receiverId: chefId,
        message: newMessage,
        timestamp: timestamp,
        imageUrls: [],
        isSeen: false,
        isSending: false,
        isFirstMessage: false,
        isNotification: false,
        isSecondNotification: false,
        isRestricted: true,
      });
      setNewMessage("");
      setTextAreaHeight("45px");
      setSelectedImages([]);
      return;
    }
    if (!selectedChatId) {
      toast.warn("No chat selected to send message to.");
      return;
    }

    if (newMessage.trim() === "" && selectedImages.length === 0) {
      toast.warn("Cannot send an empty message.");
      return;
    }
    if (newMessage.length > 1000) {
      toast.warn("Your message exceeds the character limit (1,000).", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      return;
    }

    setNewMessage("");
    setTextAreaHeight("45px");

    try {
      const receiverId = chats.find(
        (chat) => chat._id == selectedChatId
      )?.otherUserId;
      const chefProfileImage = chef.profileImage;
      const chefFullName = chef.fullName;

      let tempMessages = [];
      let firestoreRefs = [];

      if (selectedImages.length === 0 && newMessage.trim() !== "") {
        const uniqueId = generateUniqueId();
        const tempMessage = {
          senderId: LoginData.chefDetails._id,
          receiverId,
          message: newMessage,
          timestamp: timestamp,
          isSeen: false,
          senderName: chef.firstName,
          senderImage: chefProfileImage,
          imagePath: [],
          isFirstMessage: false,
          isNotification: false,
          isTemp: true,
          isLoading: false,
          isRestricted: false,
        };
        const messagesRef = collection(db, "rooms", selectedChatId, "messages");
        let docRef;
        try {
          docRef = await addDoc(messagesRef, tempMessage);
        } catch (error) {
          new Error("Error adding message: " + error);
        }

        const finalMessage = { ...tempMessage, id: docRef.id };
        tempMessages.push(finalMessage);

        const formData = new FormData();
        formData.append("message", newMessage);
        formData.append("userId", LoginData.chefDetails._id);
        formData.append("chefFullName", chefFullName);
        formData.append("chefProfileImage", chefProfileImage);
        formData.append("receiverId", receiverId);
        formData.append("roomId", roomId || selectedChatId);
        try {
          const response = await axiosInstance().post(
            handleMessageNotification,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (response.data.status === 1) {
            try {
              await updateDoc(
                doc(db, "rooms", selectedChatId, "messages", docRef.id),
                {
                  isLoading: true,
                }
              );
            } catch (error) {
              new Error("Error updating document: " + error);
            }
          } else {
            new Error("Error sending text message notification");
          }
        } catch (err) {
          new Error("Text message notification error: " + err);
        }
      }

      const validImages = selectedImages.filter((image) =>
        image.type.startsWith("image/")
      );

      if (validImages.length !== selectedImages.length) {
        toast.warn("Only image files are allowed.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });

        setSelectedImages(validImages);

        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      for (let i = 0; i < validImages.length; i++) {
        const image = validImages[i];
        const uniqueId = generateUniqueId();

        const messageText = i === 0 ? newMessage.trim() || "" : "";
        const tempMessage = {
          senderId: LoginData.chefDetails._id,
          receiverId,
          message: messageText,
          timestamp: serverTimestamp(),
          isSeen: false,
          senderName: chef.firstName,
          senderImage: chefProfileImage,
          imagePath: [URL.createObjectURL(image)],
          isFirstMessage: false,
          isNotification: false,
          isTemp: true,
          isLoading: true,
          isRestricted: false,
        };

        const messagesRef = collection(db, "rooms", selectedChatId, "messages");
        let docRef;
        try {
          docRef = await addDoc(messagesRef, tempMessage);
        } catch (error) {
          new Error("Error adding image message: " + error);
        }

        const finalMessage = { ...tempMessage, id: docRef.id };
        tempMessages.push(finalMessage);
        firestoreRefs.push({ docId: docRef.id, image });
      }

      setSelectedImages([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      scrollToBottom();

      for (let i = 0; i < firestoreRefs.length; i++) {
        const { docId, image } = firestoreRefs[i];
        const formData = new FormData();
        const path = `messages/chefs/${roomId}/${LoginData.chefDetails._id}`;
        formData.append("imagePath", path);
        formData.append("images", image);
        formData.append("userId", LoginData.chefDetails._id);
        formData.append("chefFullName", chefFullName);
        formData.append("chefProfileImage", chefProfileImage);
        formData.append("receiverId", receiverId);
        formData.append("roomId", roomId || selectedChatId);

        try {
          const response = await axiosInstance().post(
            handleMessageNotification,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (response.data.status === 1 && Array.isArray(response.data.data)) {
            const imagePath = response.data.data;

            try {
              await updateDoc(
                doc(db, "rooms", selectedChatId, "messages", docId),
                {
                  imagePath,
                  isLoading: false,
                  isTemp: false,
                }
              );
            } catch (error) {
              new Error("Error updating doc with imagePath: " + error);
            }

            setMessages((prevMessages) =>
              prevMessages.map((msg) =>
                msg.id === docId
                  ? { ...msg, imagePath, isLoading: false, isTemp: false }
                  : msg
              )
            );
          } else {
            new Error("Error uploading image: " + response.data);

            try {
              await updateDoc(
                doc(db, "rooms", selectedChatId, "messages", docId),
                {
                  isLoading: false,
                }
              );
            } catch (error) {
              new Error("Error updating doc after upload fail: " + error);
            }

            setMessages((prevMessages) =>
              prevMessages.map((msg) =>
                msg.id === docId ? { ...msg, isLoading: false } : msg
              )
            );
          }
        } catch (err) {
          new Error("Upload error: " + err);

          try {
            await updateDoc(
              doc(db, "rooms", selectedChatId, "messages", docId),
              {
                isLoading: false,
              }
            );
          } catch (error) {
            new Error("Error updating doc after API error: " + err);
          }

          setMessages((prevMessages) =>
            prevMessages.map((msg) =>
              msg.id === docId ? { ...msg, isLoading: false } : msg
            )
          );
        }
      }
    } catch (error) {
      new Error("Error sending message: " + error);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      setShowEmojiPicker(false);
      sendMessage();
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    }

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    if (messageSearchTerm.trim() === "") {
      setSearchHighlights([]);
      setCurrentHighlightIndex(-1);
      setNoHighlightsMessage("");
      setVisible(false);
      return;
    }

    const highlights = [];
    messages.forEach((msg, index) => {
      if (
        msg.message?.toLowerCase().includes(messageSearchTerm.toLowerCase())
      ) {
        highlights.push(index);
      }
    });

    setSearchHighlights(highlights);
    setCurrentHighlightIndex(highlights.length > 0 ? 0 : -1);
  }, [messageSearchTerm, messages]);

  const handleMessageSearchChange = (event) => {
    setMessageSearchTerm(event.target.value);
  };
  const goToPreviousHighlight = () => {
    if (searchHighlights.length === 0) {
      showNoHighlightsMessage();
      return;
    }

    if (currentHighlightIndex <= 0) {
      showNoHighlightsMessage();
      return;
    }

    setCurrentHighlightIndex((prevIndex) => prevIndex - 1);
  };

  const goToNextHighlight = () => {
    if (searchHighlights.length === 0) {
      showNoHighlightsMessage();
      return;
    }

    if (currentHighlightIndex >= searchHighlights.length - 1) {
      showNoHighlightsMessage();
      return;
    }

    setCurrentHighlightIndex((prevIndex) => prevIndex + 1);
  };
  useEffect(() => {
    if (
      searchHighlights.length > 0 &&
      currentHighlightIndex >= 0 &&
      currentHighlightIndex < searchHighlights.length
    ) {
      scrollToHighlight(searchHighlights[currentHighlightIndex]);
    }
  }, [currentHighlightIndex, searchHighlights]);

  const scrollToHighlight = (messageIndex) => {
    const element = document.querySelector(`#message-${messageIndex}`);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  };
  const clearMessageSearchTerm = () => {
    setMessageSearchTerm("");
    setSearchHighlights([]);
    setCurrentHighlightIndex(-1);
    setIsMessageSearching(false);
    setNoHighlightsMessage("");
    setVisible(false);
  };
  const toggleMessageSearch = () => {
    setIsMessageSearching(!isMessageSearching);
    if (!isMessageSearching) {
      setMessageSearchTerm("");
      setSearchHighlights([]);
      setCurrentHighlightIndex(-1);
    }
  };

  const scrollToBottom = () => {
    if (chatBottomRef.current) {
      const isMobile = window.innerWidth <= 768;
      const messageArea = document.querySelector(".message-area");
      if (messageArea) {
        messageArea.scrollTo({
          top: messageArea.scrollHeight,
          behavior: isMobile ? "smooth" : "instant",
        });
      }
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);
  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";

    try {
      const date =
        typeof timestamp.toDate === "function" ? timestamp.toDate() : timestamp;

      return moment(date).format("h:mm a");
    } catch (err) {
      new Error("Invalid timestamp format: " + timestamp);
    }
  };

  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));
    const nonImageFiles = files.filter(
      (file) => !file.type.startsWith("image/")
    );

    if (nonImageFiles.length > 0) {
      toast.warn("Only image files are allowed.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }

    setSelectedImages((prevImages) => [...prevImages, ...imageFiles]);
    inputRef.current?.focus();
    event.target.value = null;
  };

  const triggerImageSelection = () => {
    fileInputRef.current.click();
  };

  const removeSelectedImage = (indexToRemove) => {
    setSelectedImages((prevImages) =>
      prevImages.filter((_, index) => index !== indexToRemove)
    );
  };

  const removeAllSelectedImages = () => {
    setSelectedImages([]);
  };

  const shouldShowLoader = (imageUrl) => {
    if (typeof imageUrl === "string" && imageUrl.startsWith("public/")) {
      return false;
    }
    return true;
  };

  const formatDateHeader = (date) => {
    if (!date) return null;

    const now = moment();
    const messageDate = moment(date);

    if (now.isSame(messageDate, "day")) {
      return "Today";
    }

    if (now.clone().subtract(1, "day").isSame(messageDate, "day")) {
      return "Yesterday";
    }

    const lastSunday = now.clone().startOf("week");

    if (messageDate.isSameOrAfter(lastSunday)) {
      return messageDate.format("dddd");
    }

    return messageDate.format("MMM D, YYYY");
  };
  const getHighlightedText = (text, highlight) => {
    if (!highlight.trim()) {
      return text;
    }

    const parts = text.split(new RegExp(`(${escapeRegExp(highlight)})`, "gi"));
    return (
      <span>
        {parts.map((part, i) =>
          part.toLowerCase() === highlight.toLowerCase() ? (
            <mark key={i} style={{ padding: 0, margin: 0 }}>
              {part}
            </mark>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  const showNoHighlightsMessage = () => {
    setNoHighlightsMessage("No messages found.");
    setVisible(true);
    setTimeout(() => {
      setVisible(false);
    }, 2000);
  };
  const allMessages = [...messages, ...restrictedMessages].sort((a, b) => {
    const aTime = a.timestamp?.toDate?.() || a.timestamp;
    const bTime = b.timestamp?.toDate?.() || b.timestamp;
    return aTime - bTime;
  });

  useEffect(() => {
    if (chatListLoading) {
      const timer = setTimeout(() => {
        if (chatListLoading) {
          setHasLoaded(true);
          setChatListLoading(false);
        }
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [chatListLoading]);

  return (
    <div className="my-chat-container">
      <div className={`chat-list-wrapper ${isChatListOpen ? "open" : "close"}`}>
        <div
          className="mobile-menu-toggle"
          onClick={toggleChatList}
          style={{
            ...(isChatListOpen
              ? { right: "10px" }
              : { left: "2px", right: "auto", top: "80px", display: "none" }),
          }}
        >
          {isChatListOpen ? (
            <FontAwesomeIcon
              icon={faTimes}
              style={{ color: "white", cursor: "pointer", zIndex: 1000 }}
            />
          ) : (
            <FontAwesomeIcon
              icon={faBars}
              style={{ color: "white", cursor: "pointer", zIndex: 1000 }}
            />
          )}
        </div>
        <div className="my-chat-header">
          <div className="dash-head">
            <h3 style={{ textTransform: "none" }}>My Messages</h3>
          </div>
        </div>
        <div
          className="search-bar"
          style={{ backgroundColor: "#e8e6e6", position: "relative" }}
        >
          <FontAwesomeIcon
            icon={faSearch}
            aria-label="Search"
            style={{
              color: "black",
              position: "absolute",
              left: "30px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          />
          <input
            type="search"
            placeholder="Search Customer..."
            value={searchTerm}
            onChange={handleSearchChange}
            aria-label="Search Customer"
            className="message-search-input"
            ref={searchInputRef}
            style={{ paddingLeft: "30px" }}
          />
        </div>
        <div className="chat-list">
          {chatListLoading ? (
            <>
              {[...Array(5)].map((_, index) => (
                <div
                  key={`loader-${index}`}
                  className="chat-item loading"
                  style={{
                    backgroundColor: "#f2f2f2",
                    height: "70px",
                    marginBottom: "5px",
                    borderRadius: "5px",
                  }}
                >
                  <div className="loader-avatar">
                    <div className="skeleton-circle"></div>
                  </div>
                  <div className="loader-details">
                    <div className="skeleton-line"></div>
                    <div className="skeleton-line short"></div>
                  </div>
                </div>
              ))}
            </>
          ) : hasLoaded && filteredChats.length === 0 ? ( //check it has loaded without a result
            <div className="no-results">No Customer Available</div>
          ) : filteredChats.length > 0 ? (
            filteredChats?.map((chat, index) => {
              const messageDate = chat.lastMessageCreatedAt;
              let dateHeader = null;

              if (messageDate) {
                const isFirstMessageOfDay =
                  index === 0 ||
                  !previousDayRef.current ||
                  moment(messageDate).format("YYYY-MM-DD") !==
                    previousDayRef.current;

                if (isFirstMessageOfDay) {
                  dateHeader = formatDateHeader(messageDate);
                  previousDayRef.current =
                    moment(messageDate).format("YYYY-MM-DD");
                }
              }
              return (
                <React.Fragment key={chat._id}>
                  <div
                    className={`chat-item ${
                      selectedChatId === chat._id ? "selected" : ""
                    } `}
                    onClick={() => handleChatItemClick(chat._id)}
                    style={{
                      cursor: "pointer",
                      backgroundColor:
                        selectedChatId === chat._id ? "#dbdada" : "#ffffff",
                    }} //Added style for alternate background
                  >
                    <div className="chat-avatar">
                      {chat.firstUserProfileImage ? (
                        <img
                          src={ImageURL + chat.firstUserProfileImage}
                          alt="User Avatar"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faUser}
                          className="faLeftUsers"
                        />
                      )}
                    </div>
                    <div className="chat-details">
                      <div>
                        <div className="chat-name">{chat.firstUserName}</div>
                        <div className="chat-message">
                          {chat.lastMessage
                            ? chat.lastMessage.length > 25
                              ? chat.lastMessage.slice(0, 25) + "..."
                              : chat.lastMessage
                            : "No message"}
                        </div>
                      </div>
                      <div className="chat-time">
                        <div className="chat-meta">
                          <div
                            className="notification-icon"
                            style={{ cursor: "pointer" }}
                          >
                            {chat.unseenMessagesCount > 0 && (
                              <span className="notification-badge">
                                {chat.unseenMessagesCount}
                              </span>
                            )}
                          </div>
                          <div>
                            {chat.lastMessageCreatedAt
                              ? moment(chat.lastMessageCreatedAt).fromNow()
                              : ""}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          ) : null}
        </div>
      </div>

      <div className="chat-details-wrapper">
        <div className="chat-container">
          <div className="chat-header">
            <div
              className="mobile-menu-toggle"
              onClick={toggleChatList}
              style={{
                ...(isChatListOpen
                  ? { right: 0, display: "none" }
                  : { left: "2px", right: "auto", top: "80px" }),
              }}
            >
              {isChatListOpen ? (
                <FontAwesomeIcon
                  icon={faTimes}
                  style={{ color: "white", cursor: "pointer", zIndex: 1000 }}
                />
              ) : (
                <FontAwesomeIcon
                  icon={faBars}
                  style={{ color: "white", cursor: "pointer", zIndex: 1000 }}
                />
              )}
            </div>

            {otherUser && otherUser.firstUserProfileImage ? (
              <img
                src={ImageURL + otherUser.firstUserProfileImage}
                alt={otherUser?.firstUserName}
              />
            ) : (
              <FontAwesomeIcon
                icon={faUser}
                size="2x"
                color="white"
                className="chat-header-icon"
              />
            )}
            <h1>{otherUser ? otherUser?.firstUserName : "Select a Chat"}</h1>
            <div className="message-search-area">
              {isMessageSearching && hasRooms && (
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="search"
                    placeholder="Search Message..."
                    value={messageSearchTerm}
                    onChange={handleMessageSearchChange}
                    className="message-search-input input_1"
                    style={{ paddingRight: "80px" }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      right: 10,
                      display: "flex",
                      gap: "10px",
                      alignItems: "center",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faArrowUp}
                      style={{
                        color: "black",
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                      onClick={goToPreviousHighlight}
                    />
                    <FontAwesomeIcon
                      icon={faArrowDown}
                      style={{
                        color: "black",
                        cursor: "pointer",
                        fontSize: 13,
                      }}
                      onClick={goToNextHighlight}
                    />
                    <FontAwesomeIcon
                      icon={faTimes}
                      style={{
                        color: "black",
                        cursor: "pointer",
                        fontSize: 19,
                      }}
                      onClick={clearMessageSearchTerm}
                    />
                  </div>
                </div>
              )}
              {!messageSearchTerm && !isMessageSearching && hasRooms ? (
                <FontAwesomeIcon
                  icon={faSearch}
                  style={{ color: "black", cursor: "pointer" }}
                  onClick={toggleMessageSearch}
                />
              ) : null}
            </div>
          </div>

          <div className="message-area">
            <div className="no-highlight-tooltip-container">
              <Tippy
                content={noHighlightsMessage}
                visible={visible}
                placement="top"
                theme="light"
                arrow={true}
                animation="scale"
                duration={[1000, 150]}
              >
                <div style={{ height: "100%", width: "100%" }}></div>
              </Tippy>
            </div>
            {allMessages.length > 0 ? (
              allMessages.map((msg, index) => {
                const messageDate = msg.timestamp?.toDate();
                let dateHeader = null;
                const cutoffDate = moment().subtract(
                  userChatHistoryTime,
                  "days"
                );
                if (messageDate && moment(messageDate).isBefore(cutoffDate)) {
                  return null;
                }
                if (messageDate) {
                  const isFirstMessageOfDay =
                    index === 0 ||
                    !previousDayRef.current ||
                    moment(messageDate).format("YYYY-MM-DD") !==
                      previousDayRef.current;

                  if (isFirstMessageOfDay) {
                    dateHeader = formatDateHeader(messageDate);
                    previousDayRef.current =
                      moment(messageDate).format("YYYY-MM-DD");
                  }
                }

                const isSentMessage =
                  msg.senderId === LoginData.chefDetails._id;
                const messageClass = isSentMessage ? "sent" : "received";
                const images =
                  msg.imagePath && msg.imagePath.length > 0
                    ? msg.imagePath
                    : msg.imageUrls;
                const flattenedImages = images ? images.flat() : [];

                const isHighlighted = searchHighlights.includes(index);
                const isCurrentHighlight =
                  currentHighlightIndex !== -1 &&
                  searchHighlights[currentHighlightIndex] === index;
                const selectedStyle = isCurrentHighlight
                  ? { backgroundColor: "#bdb9bb" }
                  : {};

                if (msg.isRestricted && !msg.isTemp) {
                  return null;
                }

                const contentArray =
                  flattenedImages.length > 0
                    ? flattenedImages.map((imageUrl, imageIndex) => {
                        const key = `${msg.id}-${imageIndex}`;
                        const isFirstImage = imageIndex === 0;
                        const showLoader = shouldShowLoader(imageUrl);

                        return (
                          <div
                            key={key}
                            className={`message ${messageClass} ${
                              isHighlighted ? "highlighted" : ""
                            }`}
                            id={`message-${index}`}
                            style={selectedStyle}
                          >
                            <div className="message-content">
                              <div className="message-images">
                                <div className="image-wrapper">
                                  <img
                                    src={
                                      msg.isTemp
                                        ? imageUrl
                                        : ImageURL + imageUrl
                                    }
                                    style={{ margin: "5px" }}
                                    onLoad={() => {
                                      setMessages((prevMessages) =>
                                        prevMessages.map((m) =>
                                          m.id === msg.id ? { ...m } : m
                                        )
                                      );
                                    }}
                                  />
                                  {(msg.isLoading || showLoader) && (
                                    <div className="image-loader-overlay">
                                      <FontAwesomeIcon
                                        icon={faSpinner}
                                        spin
                                        size="2x"
                                        color="white"
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>

                              {msg.isRestricted && msg.isTemp && (
                                <FontAwesomeIcon
                                  icon={faExclamationTriangle}
                                  size="2x"
                                  color="red"
                                />
                              )}

                              {isFirstImage && msg.message && (
                                <div className="text-message-content">
                                  {msg.isRestricted && msg.isTemp
                                    ? "Restricted Content"
                                    : getHighlightedText(
                                        msg.message,
                                        messageSearchTerm
                                      )}
                                </div>
                              )}

                              <div className="message-tail"></div>
                              <div className="message-profile-icon">
                                {isSentMessage ? (
                                  <img
                                    src={ImageURL + chef.profileImage}
                                    alt="Chef Profile"
                                    style={{
                                      width: "25px",
                                      height: "25px",
                                      borderRadius: "50%",
                                    }}
                                  />
                                ) : (
                                  <FontAwesomeIcon icon={faUser} />
                                )}
                              </div>
                              <div className="message-time">
                                {formatMessageTime(msg.timestamp)}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    : [
                        <div
                          key={msg.id}
                          className={`message ${messageClass} ${
                            isHighlighted ? "highlighted" : ""
                          }`}
                          id={`message-${index}`}
                          style={selectedStyle}
                        >
                          <div className="message-content">
                            {msg.isTemp && msg.isRestricted && (
                              <>
                                <FontAwesomeIcon
                                  icon={faExclamationTriangle}
                                  size="2x"
                                  color="red"
                                />
                                {msg.message && (
                                  <div className="text-message-content">
                                    {"Restricted Content"}
                                  </div>
                                )}
                              </>
                            )}
                            {msg.message && !msg.isRestricted && (
                              <div className="text-message-content">
                                {getHighlightedText(
                                  msg.message,
                                  messageSearchTerm
                                )}
                              </div>
                            )}
                            <div className="message-tail"></div>
                            <div className="message-profile-icon">
                              {isSentMessage ? (
                                <img
                                  src={ImageURL + chef.profileImage}
                                  alt="Chef Profile"
                                  style={{
                                    width: "25px",
                                    height: "25px",
                                    borderRadius: "50%",
                                  }}
                                />
                              ) : (
                                <FontAwesomeIcon icon={faUser} />
                              )}
                            </div>
                            <div className="message-time">
                              {formatMessageTime(msg.timestamp)}
                            </div>
                          </div>
                        </div>,
                      ];

                return (
                  <React.Fragment key={msg.id}>
                    {dateHeader && (
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "12px",
                          color: "#666",
                          fontWeight: "bold",
                          margin: "5px 0",
                        }}
                      >
                        {dateHeader}
                      </div>
                    )}
                    <div
                      style={
                        isCurrentHighlight
                          ? {
                              backgroundColor: "#f7f7f7",
                              borderRadius: "5px",
                              margin: "10px 0",
                            }
                          : { margin: "10px 0" }
                      }
                      id={`message-row-${index}`}
                      className={
                        isSentMessage
                          ? "message-row-sent"
                          : "message-row-received"
                      }
                    >
                      {contentArray}
                    </div>
                  </React.Fragment>
                );
              })
            ) : (
              <div className="no-chat-history">No message found.</div>
            )}

            <div ref={chatBottomRef} />
          </div>

          {selectedImages.length > 0 && (
            <div className="preview-image-container">
              {selectedImages.map((image, index) => (
                <div key={index} className="preview-image-wrapper">
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Preview ${index}`}
                    className="preview-image"
                  />
                  <button
                    className="remove-image-button"
                    onClick={() => removeSelectedImage(index)}
                  >
                    <FontAwesomeIcon icon={faTimes} color="white" />
                  </button>
                </div>
              ))}
              <button
                className="remove-all-images-button"
                onClick={removeAllSelectedImages}
              >
                <FontAwesomeIcon icon={faTimes} color="white" />
              </button>
            </div>
          )}

          <div className="input-area">
            <button
              className="emoji-button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              disabled={!selectedChatId}
            >
              😊
            </button>
            {showEmojiPicker && (
              <div className="emoji-picker-container" ref={emojiPickerRef}>
                <Picker data={data} onEmojiSelect={handleEmojiClick} />
              </div>
            )}
            <button
              className="attach-file-button"
              onClick={triggerImageSelection}
              disabled={!selectedChatId}
            >
              <FontAwesomeIcon icon={faPaperclip} />
            </button>

            {/* Modified input field to textarea */}
            <textarea
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={handleInputChange}
              placeholder="Type your message..."
              className="input-field type-field"
              onKeyDown={handleKeyDown}
              disabled={!selectedChatId}
              style={{
                height: textAreaHeight,
                maxHeight: maxTextAreaHeight,
                resize: "none",
                overflowY: "hidden",
                minHeight: "45px",
              }}
            />

            <input
              type="file"
              style={{ display: "none" }}
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              ref={fileInputRef}
            />

            <button
              onClick={sendMessage}
              className="send-button"
              disabled={!selectedChatId}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

MyChat.propTypes = {
  initialChats: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      fullName: PropTypes.string.isRequired,
      lastMessage: PropTypes.string,
      lastMessageCreatedAt: PropTypes.string,
      unseenMessagesCount: PropTypes.number,
    })
  ),
};

export default MyChat;
